/**
 * Approvals — pending inbox + submitted history + admin rules configuration.
 * Uses /approvals-v2/* endpoints (new richer engine); legacy /approvals/pending
 * (purchase-order maker-checker) is untouched.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { fmtINR, fmtDate } from "@/lib/format";
import { CheckCircle2, XCircle, CornerUpLeft, Plus, Trash2, ShieldCheck, Coins, FileDown, UserCog, CalendarClock, Ban } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";

const VOUCHER_TYPES = ["sales_invoice","purchase_bill","payment","receipt",
  "journal_voucher","purchase_order","credit_note","debit_note"];
const STATUS_COLOR = { pending: "bg-amber-500", approved: "bg-emerald-500",
  rejected: "bg-red-500", returned: "bg-blue-500" };

export default function Approvals() {
  const { user } = useAuth();
  const isAdmin = ["admin","owner","business_owner","super_admin"].includes(user?.role);
  const [inbox, setInbox] = useState({ pending: [], submitted: [] });
  const [rules, setRules] = useState([]);
  const [paymentPending, setPaymentPending] = useState([]);
  const [paySelected, setPaySelected] = useState(new Set());
  const [delegations, setDelegations] = useState({ outbound: [], inbound: [] });
  const [orgUsers, setOrgUsers] = useState([]);
  const [newDele, setNewDele] = useState({ delegate_user_id: "", from_date: "", to_date: "", note: "" });
  const [nr, setNr] = useState({ voucher_type: "sales_invoice", threshold_amount: 0,
    mode: "hierarchical", approver_user_ids: [], enabled: true });

  const load = async () => {
    try {
      const [i, r, ps, dele, us] = await Promise.all([
        api.get("/approvals-v2/inbox"),
        isAdmin ? api.get("/approvals-v2/rules") : Promise.resolve({ data: { items: [] } }),
        api.get("/payment-sheet"),
        api.get("/approvals-v2/delegations"),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setInbox(i.data); setRules(r.data.items || []);
      setDelegations(dele.data || { outbound: [], inbound: [] });
      setOrgUsers((us.data || []).filter(u => u.id !== user?.id));
      // Filter payment-sheet rows that are pending/submitted approval
      const all = [
        ...(ps.data?.overdue_bills || []),
        ...(ps.data?.advance_vouchers || []),
        ...(ps.data?.requested_transactions || []),
      ];
      setPaymentPending(all.filter(row => ["pending", "submitted"].includes(row.approval_status)));
      setPaySelected(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const act = async (id, action) => {
    const remark = prompt(`${action} remark (optional):`) || "";
    try { await api.post(`/approvals-v2/${id}/${action}`, { remark }); toast.success(action); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addRule = async () => {
    try { await api.post("/approvals-v2/rules", nr); toast.success("Rule added"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const delRule = async (id) => {
    try { await api.delete(`/approvals-v2/rules/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Iter-132 · Delegated Approval CRUD
  const createDelegation = async () => {
    if (!newDele.delegate_user_id || !newDele.from_date || !newDele.to_date) {
      return toast.error("Pick delegate + from/to dates");
    }
    if (newDele.from_date > newDele.to_date) {
      return toast.error("From date must be on or before To date");
    }
    try {
      const r = await api.post("/approvals-v2/delegations", newDele);
      const fwd = r.data?.forwarded_now || 0;
      toast.success(fwd > 0 ? `Delegation saved · ${fwd} pending approval(s) auto-forwarded` : "Delegation saved");
      setNewDele({ delegate_user_id: "", from_date: "", to_date: "", note: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const cancelDelegation = async (id) => {
    if (!window.confirm("Cancel this delegation? Any active window ends immediately.")) return;
    try { await api.delete(`/approvals-v2/delegations/${id}`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const isActive = (d) => (d.enabled !== false) && d.from_date <= today && today <= d.to_date;
  const activeOutbound = delegations.outbound.filter(isActive);

  // Iter-106 · payment-sheet bulk approve
  const togglePay = (id) => {
    const next = new Set(paySelected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPaySelected(next);
  };
  const bulkApprovePay = async () => {
    if (paySelected.size === 0) return toast.error("Pick at least one row");
    if (!isAdmin) return toast.error("Only Admin / Owner can bulk-approve");
    if (!window.confirm(`Approve ${paySelected.size} payment row(s)?`)) return;
    let ok = 0, fail = 0;
    for (const id of paySelected) {
      try { await api.post(`/payment-sheet/mark-approved/${encodeURIComponent(id)}`); ok++; }
      catch { fail++; }
    }
    toast.success(`Approved ${ok} · Failed ${fail}`); load();
  };
  const downloadPayableList = () => {
    const a = document.createElement("a");
    a.href = `${API}/payment-sheet/payable-list.xlsx`;
    a.target = "_blank"; a.rel = "noopener";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="p-6 space-y-6" data-testid="approvals-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <ShieldCheck className="w-8 h-8 text-emerald-500" /> Approvals
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Threshold-based transaction approvals · hierarchical (branch manager) or user-specific · approve / reject / return-with-remarks · email + in-app.
        </p>
      </div>

      {activeOutbound.length > 0 && (
        <div className="rounded-sm border border-blue-500/40 bg-blue-500/5 p-3 flex items-start gap-3 text-sm" data-testid="dele-active-banner">
          <UserCog className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-medium">
              You have {activeOutbound.length} active delegation{activeOutbound.length > 1 ? "s" : ""}.
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Your approvals are auto-forwarding to{" "}
              {activeOutbound.map((d, i) => (
                <span key={d.id}>
                  <b>{d.delegate_name || d.delegate_user_id}</b>
                  {" "}
                  <span className="font-mono text-[10px]">({d.from_date} → {d.to_date})</span>
                  {i < activeOutbound.length - 1 && " · "}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            My Inbox{inbox.pending.length > 0 && <Badge className="ml-2">{inbox.pending.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payment-approvals">
            <Coins className="w-3.5 h-3.5 mr-1" /> Payment Approvals{paymentPending.length > 0 && <Badge className="ml-2 bg-amber-500">{paymentPending.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="submitted" data-testid="tab-submitted">My Submitted</TabsTrigger>
          <TabsTrigger value="delegations" data-testid="tab-delegations">
            <UserCog className="w-3.5 h-3.5 mr-1" /> Delegations
            {activeOutbound.length > 0 && <Badge className="ml-2 bg-blue-500">{activeOutbound.length}</Badge>}
          </TabsTrigger>
          {isAdmin && <TabsTrigger value="rules" data-testid="tab-rules">Rules</TabsTrigger>}
        </TabsList>

        <TabsContent value="pending" className="mt-4"><Card><CardContent className="pt-6">
          {inbox.pending.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6">All caught up.</div>
          ) : (
            <table className="w-full dense text-sm"><thead className="text-xs text-muted-foreground border-b">
              <tr><th className="text-left py-2">Type</th><th className="text-left">Details</th>
                  <th className="text-right">Amount</th><th className="text-right pr-2">Actions</th></tr>
            </thead><tbody>{inbox.pending.map((a) => (
              <tr key={a.id} className="border-b">
                <td className="py-2 align-top">
                  <span className="text-[10px] font-mono uppercase tracking-wide">{a.voucher_type}</span>
                  {a.preview?.rule_name && (
                    <div className="text-[10px] text-amber-600 mt-0.5">via {a.preview.rule_name}</div>
                  )}
                </td>
                <td className="align-top py-2">
                  {a.preview?.kind === "bank_auto_post" ? (
                    <div className="space-y-0.5">
                      <div className="text-xs">
                        <span className="font-medium">{a.preview.bank_description || "Bank auto-post"}</span>
                        {a.preview.bank_date && <span className="ml-2 text-[10px] text-muted-foreground">{a.preview.bank_date}</span>}
                      </div>
                      <div className="text-[10px] text-muted-foreground font-mono">
                        Dr <span className="text-foreground">{a.preview.counter_account?.code} · {a.preview.counter_account?.name}</span>
                        <span className="mx-1">/</span>
                        Cr <span className="text-foreground">{a.preview.bank_account?.code} · {a.preview.bank_account?.name}</span>
                        {a.preview.voucher_type === "receipt" && <span className="ml-1 italic">(receipt: inverted)</span>}
                      </div>
                      {a.preview.submitted_by && (
                        <div className="text-[10px] text-muted-foreground">
                          Submitted by {a.preview.submitted_by.full_name || a.preview.submitted_by.email}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="font-mono text-xs">{a.voucher_id?.slice(0, 12)}</span>
                  )}
                </td>
                <td className="text-right tabular-nums align-top py-2">{fmtINR(a.amount)}</td>
                <td className="text-right space-x-1 pr-2 align-top py-2">
                  <Button size="sm" onClick={() => act(a.id, "approve")} data-testid={`btn-approve-${a.id}`}><CheckCircle2 className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => act(a.id, "return")} data-testid={`btn-return-${a.id}`}><CornerUpLeft className="w-3 h-3" /></Button>
                  <Button size="sm" variant="destructive" onClick={() => act(a.id, "reject")} data-testid={`btn-reject-${a.id}`}><XCircle className="w-3 h-3" /></Button>
                </td>
              </tr>))}</tbody></table>
          )}
        </CardContent></Card></TabsContent>

        <TabsContent value="submitted" className="mt-4"><Card><CardContent className="pt-6">
          <table className="w-full dense text-sm"><thead className="text-xs text-muted-foreground border-b">
            <tr><th className="text-left py-2">Type</th><th className="text-right">Amount</th>
                <th>Status</th><th className="text-left">Submitted</th></tr>
          </thead><tbody>
            {inbox.submitted.length === 0 && <tr><td colSpan={4} className="text-center text-muted-foreground py-6">Nothing submitted yet.</td></tr>}
            {inbox.submitted.map((a) => (
              <tr key={a.id} className="border-b">
                <td className="py-2">{a.voucher_type}</td>
                <td className="text-right tabular-nums">{fmtINR(a.amount)}</td>
                <td><Badge className={STATUS_COLOR[a.status]}>{a.status}</Badge></td>
                <td className="text-xs">{fmtDate(a.created_at)}</td>
              </tr>))}
          </tbody></table>
        </CardContent></Card></TabsContent>

        <TabsContent value="payments" className="mt-4" data-testid="tab-payments-content">
          <Card><CardHeader className="flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2"><Coins className="w-4 h-4 text-amber-600" />Payment Approvals · Bulk queue</CardTitle>
              <div className="text-xs text-muted-foreground mt-1">Overdue bills + advance vouchers + manually-requested payments awaiting sign-off. Approved rows auto-flow to the payable list Excel.</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={downloadPayableList} className="rounded-sm gap-1" data-testid="pay-download-xlsx"><FileDown className="w-3.5 h-3.5" /> Payable list · Excel</Button>
              {isAdmin && (
                <Button size="sm" onClick={bulkApprovePay} disabled={paySelected.size === 0}
                        className="rounded-sm gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pay-bulk-approve">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Bulk approve ({paySelected.size})
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {paymentPending.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-8" data-testid="pay-empty">No payment rows awaiting approval.</div>
            ) : (
              <div className="overflow-auto">
                <table className="w-full dense text-sm"><thead className="text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="text-left" style={{ width: 30 }}>
                      <input type="checkbox" checked={paymentPending.length > 0 && paymentPending.every(r => paySelected.has(r.id))}
                             onChange={() => {
                               const all = paymentPending.every(r => paySelected.has(r.id));
                               const next = new Set(paySelected);
                               paymentPending.forEach(r => { if (all) next.delete(r.id); else next.add(r.id); });
                               setPaySelected(next);
                             }} data-testid="pay-toggle-all" />
                    </th>
                    <th className="text-left py-2">Source</th>
                    <th className="text-left">Vendor</th>
                    <th className="text-left">Bill</th>
                    <th className="text-left">Due</th>
                    <th className="text-right">Amount</th>
                    <th className="text-right">Net Payable</th>
                    <th>Bank / IFSC</th>
                    <th>Status</th>
                  </tr>
                </thead><tbody>
                  {paymentPending.map((row) => (
                    <tr key={row.id} className={"border-b " + (paySelected.has(row.id) ? "bg-amber-500/5" : "")} data-testid={`pay-row-${row.id}`}>
                      <td><input type="checkbox" checked={paySelected.has(row.id)} onChange={() => togglePay(row.id)} data-testid={`pay-check-${row.id}`} /></td>
                      <td className="text-[10px] font-mono uppercase text-muted-foreground">{row.kind?.replace("_", " ")}</td>
                      <td>
                        <div className="font-medium">{row.vendor_name || "—"}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">{row.vendor_gstin || "—"}</div>
                      </td>
                      <td className="font-mono text-xs">{row.bill_no || "—"}</td>
                      <td className="text-xs">{row.due_date}</td>
                      <td className="text-right tabular-nums">{fmtINR(row.amount)}</td>
                      <td className="text-right tabular-nums font-medium">{fmtINR(row.net_payable)}</td>
                      <td className="text-[10px]">
                        <div className="font-mono">{row.bank_name || "—"}</div>
                        <div className="text-muted-foreground">{row.ifsc || "—"}</div>
                      </td>
                      <td><span className="text-[10px] font-mono uppercase text-amber-700">{row.approval_status}</span></td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="delegations" className="mt-4" data-testid="tab-delegations-content">
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Outbound — what I've delegated */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <CalendarClock className="w-4 h-4 text-blue-500" />
                  Delegate my approvals
                </CardTitle>
                <div className="text-xs text-muted-foreground mt-1">
                  Going on leave? Pick a colleague and a date window — every approval routed to you during that window will auto-forward to them (up to 3 chained delegations).
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Delegate approvals to</Label>
                  <select
                    value={newDele.delegate_user_id}
                    onChange={(e) => setNewDele({ ...newDele, delegate_user_id: e.target.value })}
                    className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background"
                    data-testid="dele-user"
                  >
                    <option value="">— Pick a colleague —</option>
                    {orgUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.full_name || u.email} {u.role_code ? `· ${u.role_code}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">From date</Label>
                    <Input
                      type="date"
                      value={newDele.from_date}
                      onChange={(e) => setNewDele({ ...newDele, from_date: e.target.value })}
                      className="mt-1"
                      data-testid="dele-from"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">To date</Label>
                    <Input
                      type="date"
                      value={newDele.to_date}
                      onChange={(e) => setNewDele({ ...newDele, to_date: e.target.value })}
                      className="mt-1"
                      data-testid="dele-to"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Note (optional)</Label>
                  <Input
                    placeholder="e.g. Vacation · Diwali holidays"
                    value={newDele.note}
                    onChange={(e) => setNewDele({ ...newDele, note: e.target.value })}
                    className="mt-1"
                    data-testid="dele-note"
                  />
                </div>
                <Button
                  onClick={createDelegation}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                  data-testid="dele-save"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Set up delegation
                </Button>
                <div className="text-[10px] text-muted-foreground">
                  A new delegation replaces any previous outbound one. Also auto-forwards pending approvals if the window is active now.
                </div>
              </CardContent>
            </Card>

            {/* Active + past delegations */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">My delegations</CardTitle>
                <div className="text-xs text-muted-foreground mt-1">
                  Outbound (I delegated) · Inbound (delegated to me)
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-xs font-medium mb-1.5">Outbound ({delegations.outbound.length})</div>
                  {delegations.outbound.length === 0 ? (
                    <div className="text-xs text-muted-foreground border rounded p-3 text-center" data-testid="dele-outbound-empty">
                      You haven't delegated approvals to anyone.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {delegations.outbound.map((d) => (
                        <div
                          key={d.id}
                          className={"flex items-center justify-between border rounded p-2.5 text-sm " + (isActive(d) ? "bg-blue-500/5 border-blue-500/40" : "opacity-60")}
                          data-testid={`dele-out-${d.id}`}
                        >
                          <div>
                            <div className="font-medium">→ {d.delegate_name || d.delegate_user_id}</div>
                            <div className="text-[10px] text-muted-foreground font-mono">
                              {d.from_date} → {d.to_date}
                              {isActive(d) && <span className="ml-2 text-blue-600">● Active now</span>}
                              {d.enabled === false && <span className="ml-2 text-red-600">Cancelled</span>}
                            </div>
                            {d.note && <div className="text-[10px] text-muted-foreground italic mt-0.5">"{d.note}"</div>}
                          </div>
                          {d.enabled !== false && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => cancelDelegation(d.id)}
                              className="h-7 gap-1 text-red-600 border-red-500/40 hover:bg-red-500/10"
                              data-testid={`dele-cancel-${d.id}`}
                            >
                              <Ban className="w-3 h-3" /> Cancel
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-xs font-medium mb-1.5">Inbound ({delegations.inbound.length})</div>
                  {delegations.inbound.length === 0 ? (
                    <div className="text-xs text-muted-foreground border rounded p-3 text-center" data-testid="dele-inbound-empty">
                      Nobody has delegated approvals to you.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {delegations.inbound.map((d) => (
                        <div
                          key={d.id}
                          className={"border rounded p-2.5 text-sm " + (isActive(d) ? "bg-emerald-500/5 border-emerald-500/40" : "opacity-60")}
                          data-testid={`dele-in-${d.id}`}
                        >
                          <div className="font-medium">← {d.from_name || d.user_id}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">
                            {d.from_date} → {d.to_date}
                            {isActive(d) && <span className="ml-2 text-emerald-600">● Active now — you're handling their approvals</span>}
                            {d.enabled === false && <span className="ml-2 text-red-600">Cancelled</span>}
                          </div>
                          {d.note && <div className="text-[10px] text-muted-foreground italic mt-0.5">"{d.note}"</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {isAdmin && (<TabsContent value="rules" className="mt-4">
          <Card><CardHeader><CardTitle>Add Approval Rule</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
              <div><Label>Voucher Type</Label>
                <select value={nr.voucher_type} onChange={(e) => setNr({...nr, voucher_type: e.target.value})}
                          className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background" data-testid="rule-voucher-type">
                  {VOUCHER_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}</select>
              </div>
              <div><Label>Threshold (₹)</Label>
                <Input type="number" value={nr.threshold_amount}
                         onChange={(e) => setNr({...nr, threshold_amount: +e.target.value})} data-testid="rule-threshold" /></div>
              <div><Label>Mode</Label>
                <select value={nr.mode} onChange={(e) => setNr({...nr, mode: e.target.value})}
                          className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background" data-testid="rule-mode">
                  <option value="hierarchical">Hierarchical</option><option value="user">Specific User</option></select>
              </div>
              <div><Label>User IDs (comma-sep)</Label>
                <Input placeholder="user-id-1,user-id-2" value={nr.approver_user_ids.join(",")}
                         onChange={(e) => setNr({...nr, approver_user_ids: e.target.value.split(",").map((s)=>s.trim()).filter(Boolean)})}
                         data-testid="rule-approver-ids" /></div>
              <Button onClick={addRule} data-testid="btn-add-rule"><Plus className="w-4 h-4 mr-1" />Add</Button>
            </CardContent></Card>

          <Card className="mt-4"><CardHeader><CardTitle>Active Rules ({rules.length})</CardTitle></CardHeader><CardContent>
            <table className="w-full dense text-sm"><thead className="text-xs text-muted-foreground border-b">
              <tr><th className="text-left py-2">Voucher</th><th className="text-right">Threshold</th>
                  <th>Mode</th><th>Approvers</th><th className="text-right pr-2">Actions</th></tr>
            </thead><tbody>
              {rules.length === 0 && <tr><td colSpan={5} className="text-center text-muted-foreground py-6">No rules — vouchers skip approval.</td></tr>}
              {rules.map((r) => (
                <tr key={r.id} className="border-b">
                  <td className="py-2">{r.voucher_type}</td>
                  <td className="text-right tabular-nums">{fmtINR(r.threshold_amount)}</td>
                  <td>{r.mode}</td>
                  <td className="text-xs font-mono">{(r.approver_user_ids||[]).join(", ") || "auto"}</td>
                  <td className="text-right pr-2"><Button size="sm" variant="outline" onClick={() => delRule(r.id)} data-testid={`btn-del-rule-${r.id}`}><Trash2 className="w-3 h-3" /></Button></td>
                </tr>))}
            </tbody></table>
          </CardContent></Card>
        </TabsContent>)}
      </Tabs>
    </div>
  );
}
